import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isNil } from 'ramda';
import { Box, Progress, SxStyleProp } from 'theme-ui';
import { getCurrentSceneId, getScenes, uploadFiles } from 'modules/scenes';
import { FileInputProps, FileInput, FileInputChildrenType } from 'shared/FileInput';
import { Scene, SceneType } from 'types/Scene';
import { centeredContent } from 'utils/mixins';
import { Preview } from './artBoard/Preview';
import useMeasure from 'react-use/lib/useMeasure';
import { getRatioFormatObject } from 'modules/ratioFormat';
import { getSceneCreationProgress } from 'modules/scenesCreationProgress';
import { SceneVolume } from 'types/sceneVolume';
import { getSceneVolume } from 'modules/sceneVolume';
import { UploadScene } from './artBoard/UploadScene';
import { setActiveTool } from 'modules/activeTool';
import { ActiveTool } from 'types/AppState';
import { getAudio } from 'modules/audio';
import { Audio } from 'types/Audio';
import { setReplaceAvailable } from 'modules/replaceSceneContent';
import analytics from 'utils/analytics';
import { Workspace } from 'types/Clipboard';
import { setCurrentWorkspace } from 'modules/workspace';
import { setCanvasSize } from 'modules/canvas';
import { ACCEPTED_CONTENT } from 'constants/filetypes';
import { FloatingSettings } from './FloatingSettings';
import { getDownloadingProgressOfScene } from 'modules/sceneDownloadingProgress';
import { UndoRedoPanel } from './UndoRedo';
import { playingSelector, usePlayer } from 'utils/PlayerContext';

const artboardWrapperLayerStyles: SxStyleProp = {
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
};

type AfterButtonProps = {
  handleOpenPhotoSidebar: () => void;
  handleOpenVideoSidebar: () => void;
};

export type ArtBoardProps = {
  handleUpload: FileInputProps['onChange'];
  currentScene: Scene;
  scenes: Scene[];
  playing: boolean;
  isPreviewReady: boolean;
  ratio: number;
  progress: number;
  sceneVolume: SceneVolume;
  audio: Audio;
  handleCancelReplace: () => void;
  handleSetWorkspace: (workspace: Workspace) => void;
} & AfterButtonProps;

const ArtBoardBase: FC<ArtBoardProps & { width: number; height: number }> = memo(
  ({
    width,
    height,
    handleUpload,
    currentScene,
    progress,
    scenes,
    playing,
    isPreviewReady,
    sceneVolume,
    handleOpenPhotoSidebar,
    handleOpenVideoSidebar,
    audio,
    handleCancelReplace
  }) => (
    <Box
      onClick={(e: any) => {
        if (e.target.nodeName === 'CANVAS') {
          return;
        }
        handleCancelReplace();
      }}
    >
      <div
        sx={{
          pointerEvents: playing ? 'none' : 'all',
          position: 'relative',
          bg: 'background',
          width: '100%',
          height: '100%'
        }}
      >
        <div
          sx={{
            bg: 'background'
          }}
        >
          {!isPreviewReady && !playing && (
            <Box
              sx={{
                ...centeredContent,
                p: 8,
                zIndex: 'uploadScene',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                bg: 'white'
              }}
            >
              {currentScene.type === SceneType.EMPTY ? (
                <FileInput onChange={handleUpload} accept={ACCEPTED_CONTENT}>
                  {(props: FileInputChildrenType) => (
                    <UploadScene
                      {...props}
                      handleUploadClick={() => analytics.upload()}
                      handleOpenPhotoSidebar={handleOpenPhotoSidebar}
                      handleOpenVideoSidebar={handleOpenVideoSidebar}
                    />
                  )}
                </FileInput>
              ) : (
                <Progress
                  value={progress}
                  max={100}
                  sx={{ width: '50%', m: 0, color: 'primary' }}
                />
              )}
            </Box>
          )}
          <Preview
            width={width}
            height={height}
            visible={isPreviewReady || playing}
            audio={audio}
            scenes={scenes}
            playing={playing}
            sceneVolume={sceneVolume}
            handleCancelReplace={handleCancelReplace}
          />
        </div>
      </div>
    </Box>
  )
);

const ArtBoard = () => {
  const dispatch = useDispatch();

  const playing = usePlayer(playingSelector);

  const ratioFormatObject = useSelector(getRatioFormatObject);
  const ratio = ratioFormatObject?.ratio;

  const scenes = useSelector(getScenes);
  const currentSceneId = useSelector(getCurrentSceneId);
  const currentScene = useMemo(() => scenes.find(item => item.id === currentSceneId) || scenes[0], [
    scenes,
    currentSceneId
  ]);
  const creationProgress = useSelector(getSceneCreationProgress(currentSceneId));
  const downloadingProgress = useSelector(getDownloadingProgressOfScene(currentSceneId));
  const progress = creationProgress < 100 ? creationProgress : downloadingProgress;
  const isPreviewReady = currentScene.type !== SceneType.EMPTY && !isNil(currentScene.src);

  const handleUpload: FileInputProps['onChange'] = useCallback(files => {
    dispatch(uploadFiles(files));
  }, []);

  const sceneVolume = useSelector(getSceneVolume);

  const handleOpenPhotoSidebar: AfterButtonProps['handleOpenPhotoSidebar'] = () => {
    dispatch(setActiveTool(ActiveTool.PhotoSidebar));
  };

  const handleOpenVideoSidebar: AfterButtonProps['handleOpenVideoSidebar'] = () => {
    dispatch(setActiveTool(ActiveTool.StockVideos));
  };

  const handleCancelReplace = () => {
    if (creationProgress && creationProgress !== 100) {
      return;
    }

    dispatch(setReplaceAvailable(false));
  };

  const handleSetWorkspace = useCallback((workspace: Workspace) => {
    dispatch(setCurrentWorkspace(workspace));
  }, []);

  useEffect(() => {
    const closeVideoEditor = () => {
      const lastScene = scenes[scenes.length - 1];
      analytics.videoEditorClose({
        scene_count: scenes.length,
        length: Math.ceil(lastScene.start + lastScene.duration),
        aspect_ratio_changed: ratioFormatObject.ratioText
      });
    };

    window.addEventListener('beforeunload', closeVideoEditor);

    return () => {
      window.removeEventListener('beforeunload', closeVideoEditor);
    };
  }, [scenes]);

  useEffect(() => {
    !window.analytics?.isVideoEditorLoadDispatched &&
      analytics.videoEditorLoad({
        loading_time: Math.round(performance.now()),
        is_logged_in: true
      });
  }, []);

  const audio = useSelector(getAudio);
  return (
    <ArtBoardWithMeasure
      handleSetWorkspace={handleSetWorkspace}
      handleOpenPhotoSidebar={handleOpenPhotoSidebar}
      handleOpenVideoSidebar={handleOpenVideoSidebar}
      playing={playing}
      scenes={scenes}
      currentScene={currentScene}
      handleUpload={handleUpload}
      progress={progress}
      ratio={ratio}
      isPreviewReady={isPreviewReady}
      sceneVolume={sceneVolume}
      audio={audio}
      handleCancelReplace={handleCancelReplace}
    />
  );
};

const ArtBoardWithMeasure: FC<ArtBoardProps> = memo(props => {
  const [ref, { width: boxWidth, height: boxHeight }] = useMeasure();
  const dispatch = useDispatch();
  const ARTBOARD_PADDING_Y = 92;
  const ARTBOARD_PADDING_X = 40;
  const [previewDimensions, setPreviewDimensions] = useState(null);

  useEffect(() => {
    let h;
    let w;

    const width = boxWidth - ARTBOARD_PADDING_X * 2;
    const height = boxHeight - ARTBOARD_PADDING_Y * 2;

    if (height * props.ratio <= width) {
      h = height;
      w = h * props.ratio;
    } else {
      w = width;
      h = w / props.ratio;
    }

    dispatch(setCanvasSize(w, h));

    setPreviewDimensions({
      width: Math.floor(w),
      height: Math.floor(h)
    });
  }, [boxWidth, boxHeight, props.ratio]);

  return (
    <Box
      onClick={() => props.handleSetWorkspace(Workspace.Artboard)}
      sx={artboardWrapperLayerStyles}
      ref={ref}
      data-test="artBoard"
    >
      <FloatingSettings />
      {!isNil(previewDimensions) && (
        <Box
          sx={{
            width: `${previewDimensions.width}px`,
            height: `${previewDimensions.height}px`,
            position: 'relative'
          }}
        >
          <ArtBoardBase
            {...props}
            width={previewDimensions.width}
            height={previewDimensions.height}
          />
        </Box>
      )}
      <UndoRedoPanel />
    </Box>
  );
});

export default ArtBoard;
